import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Database, ExternalLink, FileText } from 'lucide-react';
import {
  MANAGED_TABLE_CONFIGS,
  parseSubmissionFields,
  type Client,
  type ManagedTableRecord,
  type ParsedSubmissionField,
} from '@oneup/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DetailFact,
  DetailFactGrid,
  DetailGroup,
  DetailRow,
  DetailsDrawerError,
  DetailsDrawerSkeleton,
  DetailsPanelBody,
} from '@/components/details-drawer';
import {
  ManagementCardGrid,
  ManagementEmptyState,
  ManagementErrorState,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementRecordCard,
  ManagementStatusBadge,
  ManagementTable,
  type ManagementActiveFilter,
  type ManagementFilterGroup,
  type ManagementFilterOption,
} from '@/components/management';
import {
  CompactListMediaFallback,
  CompactRecordList,
  CompactRecordListItem,
  RecordDetailHeading,
  RecordDetailShell,
  WorkspaceSplitView,
} from '@/components/workspace';
import { contextItem, pageContext, suggestion, useAlicePageContext } from '@/components/alice';
import { clientsApi, managedTablesApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { adminConfig } from '@/platform/config';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { useUiStore } from '@/stores/ui';

type FormsParams = {
  formId?: string;
  entryId?: string;
};

const FORM_CONFIG = MANAGED_TABLE_CONFIGS.forms;
const ENTRY_CONFIG = MANAGED_TABLE_CONFIGS.formEntries;

export function FormsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId, entryId } = useParams<FormsParams>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEntriesView = location.pathname.startsWith('/clients/forms/entries');
  const [viewMode, setViewMode] = usePersistentManagementView(
    isEntriesView ? 'formEntries' : 'forms',
    searchParams,
    setSearchParams,
    isEntriesView ? 'table' : 'cards',
  );

  const search = searchParams.get('search') ?? '';
  const organization = searchParams.get('organization') ?? 'All';
  const active = searchParams.get('active') ?? 'All';
  const source = searchParams.get('source') ?? 'All';
  const purpose = searchParams.get('purpose') ?? 'All';
  const formName = searchParams.get('form') ?? 'All';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';

  const clientsQuery = useQuery({
    queryKey: ['forms', 'clients'],
    queryFn: () => clientsApi.list(),
  });
  const formsQuery = useQuery({
    queryKey: ['managed-table', 'forms', 'all'],
    queryFn: () => managedTablesApi.list('forms', { limit: 500 }),
  });
  const entriesQuery = useQuery({
    queryKey: ['managed-table', 'formEntries', 'all'],
    queryFn: () => managedTablesApi.list('formEntries', { limit: 500 }),
    enabled: isEntriesView,
  });

  const clientMap = useMemo(
    () => buildClientMap(clientsQuery.data?.items ?? []),
    [clientsQuery.data?.items],
  );
  const forms = formsQuery.data?.items ?? [];
  const entries = entriesQuery.data?.items ?? [];

  const filteredForms = useMemo(
    () =>
      forms.filter((record) =>
        matchesFormsFilters(record, { search, organization, active, source, purpose }),
      ),
    [active, forms, organization, purpose, search, source],
  );
  const filteredEntries = useMemo(
    () =>
      entries.filter((record) =>
        matchesEntryFilters(record, { search, organization, formName, dateFrom, dateTo }),
      ),
    [dateFrom, dateTo, entries, formName, organization, search],
  );

  const formOptions = useMemo(() => buildFormOptions(entries), [entries]);
  const formFilterOptions = useMemo(() => buildFormsFilterOptions(forms, clientMap), [forms, clientMap]);
  const entryFilterOptions = useMemo(
    () => buildEntriesFilterOptions(entries, clientMap, formOptions),
    [clientMap, entries, formOptions],
  );
  const entryMetrics = useMemo(() => buildEntryMetrics(entries), [entries]);

  const setParamValue = useCallback(
    (key: string, value: string, allValue = 'All') => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!value || value === allValue) params.delete(key);
          else params.set(key, value);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback((value: string) => setParamValue('search', value, ''), [setParamValue]);
  const clearEntryDateRange = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('dateFrom');
        params.delete('dateTo');
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const openForm = useCallback(
    (id: string) => navigate(withSearch(`/clients/forms/${id}`, searchParams)),
    [navigate, searchParams],
  );
  const closeForm = useCallback(
    () => navigate(withSearch('/clients/forms', searchParams)),
    [navigate, searchParams],
  );
  const openEntry = useCallback(
    (id: string) => navigate(withSearch(`/clients/forms/entries/${id}`, searchParams)),
    [navigate, searchParams],
  );
  const closeEntry = useCallback(
    () => navigate(withSearch('/clients/forms/entries', searchParams)),
    [navigate, searchParams],
  );

  const selectedFormIndex = formId
    ? filteredForms.findIndex((record) => record.id === formId)
    : -1;
  const selectedEntryIndex = entryId
    ? filteredEntries.findIndex((record) => record.id === entryId)
    : -1;
  const selectedForm = formId
    ? filteredForms.find((record) => record.id === formId) ?? forms.find((record) => record.id === formId)
    : undefined;
  const selectedEntry = entryId
    ? filteredEntries.find((record) => record.id === entryId) ?? entries.find((record) => record.id === entryId)
    : undefined;
  const selectedRecord = isEntriesView ? selectedEntry : selectedForm;
  const selectedRecordId = isEntriesView ? entryId : formId;
  const selectedRecordType = isEntriesView ? 'form_entry' : 'form';
  const selectedRecordLabel = selectedRecord
    ? isEntriesView
      ? fieldText(selectedRecord, 'Full Name') ?? fieldText(selectedRecord, 'Email') ?? selectedRecord.id
      : fieldText(selectedRecord, 'form_name') ?? selectedRecord.id
    : selectedRecordId;
  const selectedOrganizationLabel = selectedRecord
    ? organizationLabel(selectedRecord, isEntriesView ? 'Organization_link' : 'org', clientMap)
    : undefined;
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: isEntriesView ? 'formEntries' : 'forms',
        pageTitle: isEntriesView ? 'Form Entries' : 'Forms',
        recordType: selectedRecordId ? selectedRecordType : undefined,
        recordId: selectedRecordId,
        recordLabel: selectedRecordLabel,
        organizationLabel: selectedOrganizationLabel,
        contextItems: [
          contextItem(
            isEntriesView ? 'form-entries-page' : 'forms-page',
            'page',
            isEntriesView ? 'Form Entries' : 'Forms',
            isEntriesView
              ? 'Submitted form entries with contact details, summaries, and parsed submission fields.'
              : 'External form definitions, live form URLs, preview fields, and linked entries.',
          ),
          ...(selectedRecordId
            ? [
                contextItem(
                  selectedRecordId,
                  selectedRecordType,
                  selectedRecordLabel ?? selectedRecordId,
                  isEntriesView
                    ? 'Selected form entry from EXT_FormEntries.'
                    : 'Selected external form from EXT_Forms.',
                ),
              ]
            : []),
        ],
        suggestedActions: selectedRecordId
          ? [
              suggestion(
                `${selectedRecordType}-summarize`,
                isEntriesView ? 'Summarize entry' : 'Summarize form',
                isEntriesView
                  ? 'Summarize this entry and its submitted fields.'
                  : 'Summarize this form and linked submission context.',
              ),
              suggestion(
                `${selectedRecordType}-lookup`,
                isEntriesView ? 'Find related entries' : 'Check entries',
                'Look up related form records from the managed Airtable data.',
              ),
            ]
          : [
              suggestion(
                isEntriesView ? 'form-entries-counts' : 'forms-overview',
                isEntriesView ? 'Count entries' : 'Review forms',
                isEntriesView
                  ? 'Count or filter form entries by organization, form, and date range.'
                  : 'Review active external forms and entry sources.',
              ),
            ],
      }),
    [
      isEntriesView,
      selectedOrganizationLabel,
      selectedRecordId,
      selectedRecordLabel,
      selectedRecordType,
    ],
  );
  useAlicePageContext(aliceContext);

  if (clientsQuery.isLoading || formsQuery.isLoading || (isEntriesView && entriesQuery.isLoading)) {
    return <ManagementLoadingState metricCount={3} />;
  }
  if (clientsQuery.error) {
    return <ManagementErrorState title="Couldn't load organizations" error={clientsQuery.error} />;
  }
  if (formsQuery.error) {
    return <ManagementErrorState title="Couldn't load forms" error={formsQuery.error} />;
  }
  if (isEntriesView && entriesQuery.error) {
    return <ManagementErrorState title="Couldn't load form entries" error={entriesQuery.error} />;
  }

  const page = isEntriesView ? MANAGEMENT_PAGES.formEntries : MANAGEMENT_PAGES.forms;
  const activeFilters = buildActiveFilters({
    isEntriesView,
    search,
    organization,
    organizationLabel: organization === 'All' ? undefined : clientMap.get(organization)?.name ?? organization,
    active,
    source,
    purpose,
    formName,
    dateFrom,
    dateTo,
    onClear: setParamValue,
  });
  const filters: ManagementFilterGroup[] = isEntriesView
    ? [
        {
          id: 'organization',
          label: 'Organization',
          value: organization,
          onChange: (value) => setParamValue('organization', value),
          options: entryFilterOptions.organizations,
        },
        {
          id: 'form',
          label: 'Form',
          value: formName,
          onChange: (value) => setParamValue('form', value),
          options: entryFilterOptions.forms,
        },
      ]
    : [
        {
          id: 'organization',
          label: 'Organization',
          value: organization,
          onChange: (value) => setParamValue('organization', value),
          options: formFilterOptions.organizations,
        },
        {
          id: 'active',
          label: 'Active',
          value: active,
          onChange: (value) => setParamValue('active', value),
          options: [
            { value: 'All', label: 'All' },
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ],
        },
        {
          id: 'source',
          label: 'Source',
          value: source,
          onChange: (value) => setParamValue('source', value),
          options: formFilterOptions.sources,
        },
        {
          id: 'purpose',
          label: 'Purpose',
          value: purpose,
          onChange: (value) => setParamValue('purpose', value),
          options: formFilterOptions.purposes,
        },
      ];

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: isEntriesView ? 'Search names, email, phone, summary' : 'Search forms or source pages',
        }}
        filters={filters}
        activeFilters={activeFilters}
        metrics={
          isEntriesView
            ? [
                { id: 'last24', label: 'Last 24 Hours', value: entryMetrics.last24Hours, icon: FileText },
                { id: 'last7', label: 'Last 7 Days', value: entryMetrics.last7Days, icon: FileText },
                { id: 'ytd', label: 'Year To Date', value: entryMetrics.yearToDate, icon: FileText },
              ]
            : []
        }
        showMetrics={isEntriesView}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <FormsTabs isEntriesView={isEntriesView} searchParams={searchParams} onNavigate={navigate} />
      {isEntriesView && (
        <EntryDateRangeBar
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={(value) => setParamValue('dateFrom', value, '')}
          onDateToChange={(value) => setParamValue('dateTo', value, '')}
          onClear={clearEntryDateRange}
        />
      )}

      {isEntriesView ? (
        <WorkspaceSplitView
          footer={
            <ManagementFooterSummary>
              Showing <span className="tabular-nums text-foreground">{filteredEntries.length}</span>
              {entries.length !== filteredEntries.length && (
                <>
                  {' '}of <span className="tabular-nums">{entries.length}</span> loaded
                </>
              )}
            </ManagementFooterSummary>
          }
          detail={
            entryId && (
              <EntryDetailsPanel
                entryId={entryId}
                clientMap={clientMap}
                listTitle="Form Entries"
                selectedIndex={selectedEntryIndex}
                totalRecords={filteredEntries.length}
                fallback={entries.find((record) => record.id === entryId)}
                onClose={closeEntry}
                onPrevious={() => {
                  const record = filteredEntries[selectedEntryIndex - 1];
                  if (record) openEntry(record.id);
                }}
                onNext={() => {
                  const record = filteredEntries[selectedEntryIndex + 1];
                  if (record) openEntry(record.id);
                }}
              />
            )
          }
        >
          {entryId ? (
            <EntryCompactList
              entries={filteredEntries}
              activeEntryId={entryId}
              clientMap={clientMap}
              onOpenEntry={openEntry}
            />
          ) : viewMode === 'cards' ? (
            <EntryCards entries={filteredEntries} clientMap={clientMap} onOpenEntry={openEntry} />
          ) : (
            <EntryRows entries={filteredEntries} clientMap={clientMap} onOpenEntry={openEntry} />
          )}
        </WorkspaceSplitView>
      ) : (
        <WorkspaceSplitView
          footer={
            <ManagementFooterSummary>
              Showing <span className="tabular-nums text-foreground">{filteredForms.length}</span>
              {forms.length !== filteredForms.length && (
                <>
                  {' '}of <span className="tabular-nums">{forms.length}</span> loaded
                </>
              )}
            </ManagementFooterSummary>
          }
          detail={
            formId && (
              <FormDetailsPanel
                formId={formId}
                clientMap={clientMap}
                listTitle="Forms"
                selectedIndex={selectedFormIndex}
                totalRecords={filteredForms.length}
                fallback={forms.find((record) => record.id === formId)}
                onClose={closeForm}
                onOpenEntry={openEntry}
                onPrevious={() => {
                  const record = filteredForms[selectedFormIndex - 1];
                  if (record) openForm(record.id);
                }}
                onNext={() => {
                  const record = filteredForms[selectedFormIndex + 1];
                  if (record) openForm(record.id);
                }}
              />
            )
          }
        >
          {formId ? (
            <FormCompactList
              forms={filteredForms}
              activeFormId={formId}
              clientMap={clientMap}
              onOpenForm={openForm}
            />
          ) : viewMode === 'cards' ? (
            <FormCards forms={filteredForms} clientMap={clientMap} onOpenForm={openForm} />
          ) : (
            <FormRows forms={filteredForms} clientMap={clientMap} onOpenForm={openForm} />
          )}
        </WorkspaceSplitView>
      )}
    </ManagementPage>
  );
}

function FormsTabs({
  isEntriesView,
  searchParams,
  onNavigate,
}: {
  isEntriesView: boolean;
  searchParams: URLSearchParams;
  onNavigate: (to: string) => void;
}) {
  const cleanParams = withoutViewParam(searchParams);
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border/70 bg-card p-1.5">
      <button
        type="button"
        onClick={() => onNavigate(withSearch('/clients/forms', cleanParams))}
        aria-pressed={!isEntriesView}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[12px] font-medium transition-colors',
          !isEntriesView
            ? 'bg-primary text-primary-foreground shadow-[0_1px_2px_hsl(var(--primary)/0.2)]'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <ClipboardList className="h-3.5 w-3.5" aria-hidden />
        Forms
      </button>
      <button
        type="button"
        onClick={() => onNavigate(withSearch('/clients/forms/entries', cleanParams))}
        aria-pressed={isEntriesView}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[12px] font-medium transition-colors',
          isEntriesView
            ? 'bg-primary text-primary-foreground shadow-[0_1px_2px_hsl(var(--primary)/0.2)]'
            : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <FileText className="h-3.5 w-3.5" aria-hidden />
        Form Entries
      </button>
    </div>
  );
}

function EntryDateRangeBar({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClear,
}: {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onClear: () => void;
}) {
  const hasRange = Boolean(dateFrom || dateTo);
  return (
    <Card className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="space-y-1 text-[12px] font-medium text-foreground">
          <span>Submitted from</span>
          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => onDateFromChange(event.currentTarget.value)}
            className="h-8 text-[13px]"
          />
        </label>
        <label className="space-y-1 text-[12px] font-medium text-foreground">
          <span>Submitted to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => onDateToChange(event.currentTarget.value)}
            className="h-8 text-[13px]"
          />
        </label>
      </div>
      {hasRange && (
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={onClear}>
          Clear date range
        </Button>
      )}
    </Card>
  );
}

function FormCards({
  forms,
  clientMap,
  onOpenForm,
}: {
  forms: ManagedTableRecord[];
  clientMap: Map<string, Client>;
  onOpenForm: (id: string) => void;
}) {
  if (forms.length === 0) {
    return (
      <Card>
        <ManagementEmptyState icon={Database} title="No forms match this view." body="Try clearing filters or search." />
      </Card>
    );
  }

  return (
    <ManagementCardGrid>
      {forms.map((form) => {
        const title = fieldText(form, 'form_name') ?? form.id;
        const pageUrl = fieldText(form, 'default_page_url');
        return (
          <ManagementRecordCard key={form.id} role="button" tabIndex={0} className="cursor-pointer" onClick={() => onOpenForm(form.id)}>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                  <CompactListMediaFallback label={title} className="h-10 w-10" />
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-foreground">{title}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <RelationBadge value={organizationLabel(form, 'org', clientMap)} />
                      {fieldText(form, 'source_system') && (
                        <ManagementStatusBadge variant="outline">{fieldText(form, 'source_system')}</ManagementStatusBadge>
                      )}
                    </div>
                  </div>
                </div>
                <ActiveBadge active={isActive(form)} />
              </div>
              {pageUrl && <UrlLine value={pageUrl} label={pageUrl} />}
              <div className="grid gap-2 text-xs text-muted-foreground">
                <CardMeta label="Purpose" value={fieldText(form, 'form_purpose')} />
                <CardMeta label="Last sync" value={formatDate(fieldText(form, 'last_synced'))} />
              </div>
            </div>
          </ManagementRecordCard>
        );
      })}
    </ManagementCardGrid>
  );
}

function FormRows({
  forms,
  clientMap,
  onOpenForm,
}: {
  forms: ManagedTableRecord[];
  clientMap: Map<string, Client>;
  onOpenForm: (id: string) => void;
}) {
  return (
    <ManagementTable>
      <table>
        <thead>
          <tr>
            <th>Form</th>
            <th>Organization</th>
            <th>Source</th>
            <th>Purpose</th>
            <th>Active</th>
            <th>Last Sync</th>
            <th>Page URL</th>
          </tr>
        </thead>
        <tbody>
          {forms.length === 0 && (
            <tr>
              <td colSpan={7}>
                <ManagementEmptyState icon={Database} title="No forms match this view." body="Try clearing filters or search." />
              </td>
            </tr>
          )}
          {forms.map((form) => (
            <tr key={form.id} role="button" tabIndex={0} className="cursor-pointer" onClick={() => onOpenForm(form.id)}>
              <td className="font-medium">{fieldText(form, 'form_name') ?? form.id}</td>
              <td>{organizationLabel(form, 'org', clientMap)}</td>
              <td>{fieldText(form, 'source_system') ?? <MutedDash />}</td>
              <td>{fieldText(form, 'form_purpose') ?? <MutedDash />}</td>
              <td><ActiveBadge active={isActive(form)} /></td>
              <td>{formatDate(fieldText(form, 'last_synced'))}</td>
              <td><UrlLine value={fieldText(form, 'default_page_url')} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </ManagementTable>
  );
}

function FormCompactList({
  forms,
  activeFormId,
  clientMap,
  onOpenForm,
}: {
  forms: ManagedTableRecord[];
  activeFormId: string;
  clientMap: Map<string, Client>;
  onOpenForm: (id: string) => void;
}) {
  if (forms.length === 0) {
    return (
      <Card>
        <ManagementEmptyState icon={Database} title="No forms match this view." body="Try clearing filters or search." />
      </Card>
    );
  }
  return (
    <CompactRecordList title="Forms">
      {forms.map((form) => {
        const title = fieldText(form, 'form_name') ?? form.id;
        return (
          <CompactRecordListItem
            key={form.id}
            selected={form.id === activeFormId}
            onSelect={() => onOpenForm(form.id)}
            media={<CompactListMediaFallback label={title} className="mt-0.5 h-10 w-10" />}
            title={title}
            badges={<ActiveBadge active={isActive(form)} />}
            subtitle={organizationLabel(form, 'org', clientMap)}
            meta={[
              fieldText(form, 'source_system'),
              formatDate(fieldText(form, 'last_synced')),
            ].filter(Boolean).map((item) => <span key={item}>{item}</span>)}
          />
        );
      })}
    </CompactRecordList>
  );
}

function FormDetailsPanel({
  formId,
  fallback,
  clientMap,
  listTitle,
  selectedIndex,
  totalRecords,
  onClose,
  onPrevious,
  onNext,
  onOpenEntry,
}: {
  formId: string;
  fallback?: ManagedTableRecord;
  clientMap: Map<string, Client>;
  listTitle: string;
  selectedIndex: number;
  totalRecords: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onOpenEntry: (id: string) => void;
}) {
  const query = useQuery({
    queryKey: ['managed-table', 'forms', 'detail', formId],
    queryFn: () => managedTablesApi.get('forms', formId),
  });
  const form = query.data?.item ?? fallback;
  const title = form ? fieldText(form, 'form_name') ?? form.id : 'Form';
  const liveUrl = form ? fieldText(form, 'default_page_url') : undefined;
  const relatedEntryIds = form ? relationIds(form.fields['EXT_FormEntries']) : [];
  const relatedEntriesQuery = useQuery({
    queryKey: ['managed-table', 'formEntries', 'related', formId, relatedEntryIds.join(',')],
    queryFn: () => managedTablesApi.list('formEntries', { recordIds: relatedEntryIds, limit: 100 }),
    enabled: relatedEntryIds.length > 0,
  });

  return (
    <RecordDetailShell
      title={title}
      recordType={listTitle}
      onClose={onClose}
      canGoPrevious={selectedIndex > 0}
      canGoNext={selectedIndex >= 0 && selectedIndex < totalRecords - 1}
      onPrevious={onPrevious}
      onNext={onNext}
      className="2xl:sticky 2xl:top-0"
    >
      <RecordDetailHeading
        icon={ClipboardList}
        title={title}
        badges={
          <>
            {form && <RelationBadge value={organizationLabel(form, 'org', clientMap)} />}
            {form && <ActiveBadge active={isActive(form)} />}
            {fieldText(form, 'source_system') && <Badge variant="outline">{fieldText(form, 'source_system')}</Badge>}
          </>
        }
        actions={
          liveUrl && (
            <Button asChild size="sm" className="h-8 gap-1.5">
              <a href={normalizeUrl(liveUrl)} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                Open live form
              </a>
            </Button>
          )
        }
      />
      {query.isLoading && !form && <DetailsDrawerSkeleton />}
      {query.error && !form && <DetailsDrawerError error={query.error} fallback="Could not load form details." />}
      {form && (
        <DetailsPanelBody className="space-y-5">
          <DetailFactGrid>
            <DetailFact label="External Form ID" value={fieldText(form, 'external_form_id')} />
            <DetailFact label="Source Site" value={fieldText(form, 'source_site_name')} />
            <DetailFact label="Purpose" value={fieldText(form, 'form_purpose')} />
            <DetailFact label="Last Synced" value={formatDate(fieldText(form, 'last_synced'))} />
            <DetailFact label="WP Post ID" value={fieldText(form, 'wp_post_id')} />
            <DetailFact label="Airtable ID" value={form.id} />
          </DetailFactGrid>

          <DetailGroup title="Source">
            <div className="space-y-2">
              <DetailRow label="Page" value={<UrlLine value={fieldText(form, 'default_page_url')} label={fieldText(form, 'default_page_url')} />} />
              <DetailRow label="Site URL" value={<UrlLine value={fieldText(form, 'source_site_url')} label={fieldText(form, 'source_site_url')} />} />
              <DetailRow label="Form ID" value={fieldText(form, 'form_id')} />
            </div>
          </DetailGroup>

          <FormPreview form={form} />

          <DetailGroup title="Form Entries">
            <RelatedEntriesTable
              entries={relatedEntriesQuery.data?.items ?? []}
              loading={relatedEntriesQuery.isLoading}
              error={relatedEntriesQuery.error}
              clientMap={clientMap}
              onOpenEntry={onOpenEntry}
              empty={relatedEntryIds.length === 0 ? 'No linked entries.' : 'No linked entries loaded.'}
            />
          </DetailGroup>
        </DetailsPanelBody>
      )}
    </RecordDetailShell>
  );
}

function EntryRows({
  entries,
  clientMap,
  onOpenEntry,
}: {
  entries: ManagedTableRecord[];
  clientMap: Map<string, Client>;
  onOpenEntry: (id: string) => void;
}) {
  return (
    <ManagementTable>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Organization</th>
            <th>Form</th>
            <th>Name</th>
            <th>Phone</th>
            <th>Email</th>
            <th>AI Summary</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={7}>
                <ManagementEmptyState icon={Database} title="No form entries match this view." body="Try clearing filters or search." />
              </td>
            </tr>
          )}
          {entries.map((entry) => (
            <tr key={entry.id} role="button" tabIndex={0} className="cursor-pointer" onClick={() => onOpenEntry(entry.id)}>
              <td>{formatEntryDate(entry)}</td>
              <td>{organizationLabel(entry, 'Organization_link', clientMap)}</td>
              <td>{fieldText(entry, 'Form Name') ?? <MutedDash />}</td>
              <td className="font-medium">{entryName(entry)}</td>
              <td><ContactLink value={fieldText(entry, 'Phone')} kind="phone" /></td>
              <td><ContactLink value={fieldText(entry, 'Email')} kind="email" /></td>
              <td><span className="block max-w-[420px] truncate">{fieldText(entry, 'Ai Entry Summary') ?? '-'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </ManagementTable>
  );
}

function EntryCards({
  entries,
  clientMap,
  onOpenEntry,
}: {
  entries: ManagedTableRecord[];
  clientMap: Map<string, Client>;
  onOpenEntry: (id: string) => void;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <ManagementEmptyState icon={Database} title="No form entries match this view." body="Try clearing filters or search." />
      </Card>
    );
  }

  return (
    <ManagementCardGrid>
      {entries.map((entry) => {
        const title = entryName(entry);
        return (
          <ManagementRecordCard key={entry.id} role="button" tabIndex={0} className="cursor-pointer" onClick={() => onOpenEntry(entry.id)}>
            <div className="space-y-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <CompactListMediaFallback label={title} className="h-10 w-10" />
                <div className="min-w-0">
                  <div className="truncate font-semibold text-foreground">{title}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <RelationBadge value={organizationLabel(entry, 'Organization_link', clientMap)} />
                    {fieldText(entry, 'Form Name') && <ManagementStatusBadge variant="outline">{fieldText(entry, 'Form Name')}</ManagementStatusBadge>}
                  </div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">{formatEntryDate(entry)}</div>
              <p className="line-clamp-3 text-[13px] leading-5 text-foreground">{fieldText(entry, 'Ai Entry Summary') ?? 'No summary available.'}</p>
            </div>
          </ManagementRecordCard>
        );
      })}
    </ManagementCardGrid>
  );
}

function EntryCompactList({
  entries,
  activeEntryId,
  clientMap,
  onOpenEntry,
}: {
  entries: ManagedTableRecord[];
  activeEntryId: string;
  clientMap: Map<string, Client>;
  onOpenEntry: (id: string) => void;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <ManagementEmptyState icon={Database} title="No form entries match this view." body="Try clearing filters or search." />
      </Card>
    );
  }
  return (
    <CompactRecordList title="Form Entries">
      {entries.map((entry) => {
        const title = entryName(entry);
        return (
          <CompactRecordListItem
            key={entry.id}
            selected={entry.id === activeEntryId}
            onSelect={() => onOpenEntry(entry.id)}
            media={<CompactListMediaFallback label={title} className="mt-0.5 h-10 w-10" />}
            title={title}
            badges={fieldText(entry, 'Form Name') ? <ManagementStatusBadge variant="outline">{fieldText(entry, 'Form Name')}</ManagementStatusBadge> : undefined}
            subtitle={organizationLabel(entry, 'Organization_link', clientMap)}
            meta={[formatEntryDate(entry), fieldText(entry, 'Email')].filter(Boolean).map((item) => <span key={item}>{item}</span>)}
          />
        );
      })}
    </CompactRecordList>
  );
}

function EntryDetailsPanel({
  entryId,
  fallback,
  clientMap,
  listTitle,
  selectedIndex,
  totalRecords,
  onClose,
  onPrevious,
  onNext,
}: {
  entryId: string;
  fallback?: ManagedTableRecord;
  clientMap: Map<string, Client>;
  listTitle: string;
  selectedIndex: number;
  totalRecords: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}) {
  const queryClient = useQueryClient();
  const setNotice = useUiStore((state) => state.setNotice);
  const query = useQuery({
    queryKey: ['managed-table', 'formEntries', 'detail', entryId],
    queryFn: () => managedTablesApi.get('formEntries', entryId),
  });
  const entry = query.data?.item ?? fallback;
  const title = entry ? entryName(entry) : 'Form Entry';
  const parsed = useMemo(
    () => parseSubmissionFields(entry?.fields['All Fields (JSON)']),
    [entry],
  );
  const deleteMutation = useMutation({
    mutationFn: () => managedTablesApi.delete('formEntries', entryId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['managed-table', 'formEntries'] });
      setNotice('Form entry deleted');
      onClose();
    },
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : 'Delete failed', 'destructive'),
  });

  const handleDelete = () => {
    if (deleteMutation.isPending) return;
    const confirmed = window.confirm(
      'Delete this form entry from EXT_FormEntries? This permanently removes it from Airtable.',
    );
    if (confirmed) deleteMutation.mutate();
  };

  return (
    <RecordDetailShell
      title={title}
      recordType={listTitle}
      onClose={onClose}
      canGoPrevious={selectedIndex > 0}
      canGoNext={selectedIndex >= 0 && selectedIndex < totalRecords - 1}
      onPrevious={onPrevious}
      onNext={onNext}
      onDelete={entry ? handleDelete : undefined}
      deletePending={deleteMutation.isPending}
      deleteLabel="Delete"
      className="2xl:sticky 2xl:top-0"
    >
      <RecordDetailHeading
        icon={FileText}
        title={title}
        badges={
          <>
            {entry && <RelationBadge value={organizationLabel(entry, 'Organization_link', clientMap)} />}
            {fieldText(entry, 'Form Name') && <Badge variant="outline">{fieldText(entry, 'Form Name')}</Badge>}
            {entry && <span>{formatEntryDate(entry)}</span>}
          </>
        }
      />
      {query.isLoading && !entry && <DetailsDrawerSkeleton />}
      {query.error && !entry && <DetailsDrawerError error={query.error} fallback="Could not load form entry." />}
      {entry && (
        <DetailsPanelBody className="space-y-5">
          <DetailFactGrid>
            <DetailFact label="Submitted" value={formatEntryDate(entry)} />
            <DetailFact label="Form" value={fieldText(entry, 'Form Name')} />
            <DetailFact label="Organization" value={organizationLabel(entry, 'Organization_link', clientMap)} />
            <DetailFact label="Airtable ID" value={entry.id} />
          </DetailFactGrid>

          <DetailGroup title="Contact">
            <div className="space-y-2">
              <DetailRow label="Name" value={entryName(entry)} />
              <DetailRow label="Email" value={<ContactLink value={fieldText(entry, 'Email')} kind="email" />} />
              <DetailRow label="Phone" value={<ContactLink value={fieldText(entry, 'Phone')} kind="phone" />} />
              <DetailRow label="Page" value={<UrlLine value={fieldText(entry, 'Submitted Page URL')} label={fieldText(entry, 'Submitted Page Name') ?? fieldText(entry, 'Submitted Page URL')} />} />
            </div>
          </DetailGroup>

          <DetailGroup title="AI Summary">
            <LongTextBlock value={fieldText(entry, 'Ai Entry Summary') ?? 'No summary available.'} />
          </DetailGroup>

          <DetailGroup title="Submission Fields">
            {parsed.fields.length > 0 ? (
              <ParsedSubmissionFields fields={parsed.fields} />
            ) : fieldText(entry, 'All Fields Formatted') ? (
              <div className="space-y-2">
                {parsed.error && <p className="text-[12px] text-muted-foreground">JSON parsing failed; showing Airtable formatted fallback.</p>}
                <LongTextBlock value={fieldText(entry, 'All Fields Formatted')!} />
              </div>
            ) : (
              <div className="space-y-2">
                {parsed.error && <p className="text-[12px] text-muted-foreground">JSON parsing failed: {parsed.error}</p>}
                <LongTextBlock value={parsed.rawText ?? 'No submitted field data available.'} />
              </div>
            )}
          </DetailGroup>
        </DetailsPanelBody>
      )}
    </RecordDetailShell>
  );
}

function RelatedEntriesTable({
  entries,
  loading,
  error,
  clientMap,
  onOpenEntry,
  empty,
}: {
  entries: ManagedTableRecord[];
  loading: boolean;
  error: unknown;
  clientMap: Map<string, Client>;
  onOpenEntry: (id: string) => void;
  empty: string;
}) {
  if (loading) return <div className="rounded-md bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">Loading entries...</div>;
  if (error) return <div className="rounded-md bg-destructive/5 px-3 py-2 text-[12px] text-destructive">Could not load entries.</div>;
  if (entries.length === 0) return <div className="rounded-md bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">{empty}</div>;

  return (
    <ManagementTable>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Name</th>
            <th>Email</th>
            <th>AI Summary</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} role="button" tabIndex={0} className="cursor-pointer" onClick={() => onOpenEntry(entry.id)}>
              <td>{formatEntryDate(entry)}</td>
              <td>{entryName(entry)}</td>
              <td><ContactLink value={fieldText(entry, 'Email')} kind="email" /></td>
              <td><span className="block max-w-[360px] truncate">{fieldText(entry, 'Ai Entry Summary') ?? organizationLabel(entry, 'Organization_link', clientMap)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </ManagementTable>
  );
}

function FormPreview({ form }: { form: ManagedTableRecord }) {
  const rawPreview = fieldText(form, 'Form Preview') ?? fieldText(form, 'FormPreviewmirror');
  const previewHtml = useMemo(() => sanitizePreviewHtml(rawPreview), [rawPreview]);
  const fallbackFields = useMemo(
    () => extractPreviewFields(fieldText(form, 'standard_field_map') ?? fieldText(form, 'schema_json')),
    [form],
  );

  return (
    <DetailGroup title="Form Preview">
      {previewHtml ? (
        <div
          className={cn(
            'rounded-lg border border-border bg-background p-4',
            '[&_input]:min-h-8 [&_input]:w-full [&_input]:rounded-md [&_input]:border [&_input]:border-input [&_input]:bg-muted/20 [&_input]:px-2',
            '[&_select]:min-h-8 [&_select]:w-full [&_select]:rounded-md [&_select]:border [&_select]:border-input [&_select]:bg-muted/20 [&_select]:px-2',
            '[&_textarea]:min-h-20 [&_textarea]:w-full [&_textarea]:rounded-md [&_textarea]:border [&_textarea]:border-input [&_textarea]:bg-muted/20 [&_textarea]:px-2',
            '[&_label]:text-[12px] [&_label]:font-medium [&_label]:text-foreground',
          )}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      ) : fallbackFields.length > 0 ? (
        <div className="grid gap-3 rounded-lg border border-border bg-background p-4 md:grid-cols-2">
          {fallbackFields.map((field) => (
            <label key={field} className="space-y-1.5 text-[12px] font-medium text-foreground">
              <span>{field}</span>
              <input disabled className="h-8 w-full rounded-md border border-input bg-muted/20 px-2" />
            </label>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">No preview is available for this form.</div>
      )}
    </DetailGroup>
  );
}

function ParsedSubmissionFields({ fields }: { fields: ParsedSubmissionField[] }) {
  return (
    <div className="space-y-2">
      {fields.map((field) => (
        <ParsedSubmissionFieldRow key={`${field.label}-${field.value ?? field.children?.length ?? 0}`} field={field} />
      ))}
    </div>
  );
}

function ParsedSubmissionFieldRow({ field }: { field: ParsedSubmissionField }) {
  return (
    <div className="rounded-md border border-border/70 bg-background px-3 py-2">
      <div className="grid gap-1 sm:grid-cols-[180px_minmax(0,1fr)]">
        <div className="text-[12px] font-semibold text-muted-foreground">{field.label}</div>
        <div className="min-w-0 text-[13px] text-foreground">
          {field.value ? <SubmissionValue field={field} /> : <MutedDash />}
        </div>
      </div>
      {field.children && field.children.length > 0 && (
        <div className="mt-2 space-y-2 border-l border-border pl-3">
          {field.children.map((child) => (
            <ParsedSubmissionFieldRow key={`${child.label}-${child.value ?? child.children?.length ?? 0}`} field={child} />
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionValue({ field }: { field: ParsedSubmissionField }) {
  if (!field.value) return <MutedDash />;
  if (field.kind === 'email') return <ContactLink value={field.value} kind="email" />;
  if (field.kind === 'phone') return <ContactLink value={field.value} kind="phone" />;
  if (field.kind === 'url') return <UrlLine value={field.value} label={field.value} />;
  return <span className="break-words">{field.value}</span>;
}

function CardMeta({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <span className="shrink-0 font-medium">{label}</span>
      <span className="min-w-0 truncate text-foreground">{value || '-'}</span>
    </div>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return <Badge variant={active ? 'success' : 'muted'}>{active ? 'Active' : 'Inactive'}</Badge>;
}

function RelationBadge({ value }: { value?: string }) {
  if (!value) return <Badge variant="outline">No organization</Badge>;
  return <Badge variant="outline" className="max-w-full truncate">{value}</Badge>;
}

function UrlLine({ value, label = 'Open' }: { value?: string; label?: string }) {
  const href = normalizeUrl(value);
  if (!href) return <MutedDash />;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-[340px] items-center gap-1 truncate font-medium text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{label ?? href}</span>
    </a>
  );
}

function ContactLink({ value, kind }: { value?: string; kind: 'email' | 'phone' }) {
  if (!value) return <MutedDash />;
  const href = kind === 'email' ? `mailto:${value}` : `tel:${value.replace(/[^\d+]/g, '')}`;
  return (
    <a
      href={href}
      className="font-medium text-primary underline-offset-2 hover:underline"
      onClick={(event) => event.stopPropagation()}
    >
      {value}
    </a>
  );
}

function LongTextBlock({ value }: { value: string }) {
  return <div className="whitespace-pre-wrap rounded-md bg-muted/30 px-3 py-2 text-[13px] leading-relaxed">{value}</div>;
}

function MutedDash() {
  return <span className="text-muted-foreground">-</span>;
}

function buildClientMap(clients: Client[]): Map<string, Client> {
  return new Map(clients.map((client) => [client.id, client]));
}

function fieldText(record: ManagedTableRecord | undefined, field: string): string | undefined {
  return plainValue(record?.fields[field]);
}

function plainValue(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const values = value.map(plainValue).filter((item): item is string => Boolean(item));
    return values.length ? values.join(', ') : undefined;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      plainValue(record.name) ??
      plainValue(record.value) ??
      plainValue(record.label) ??
      plainValue(record.filename) ??
      plainValue(record.url)
    );
  }
  return undefined;
}

function relationIds(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value) return [value];
  return [];
}

function organizationLabel(
  record: ManagedTableRecord,
  field: 'org' | 'Organization_link',
  clientMap: Map<string, Client>,
): string | undefined {
  const ids = relationIds(record.fields[field]);
  if (ids.length === 0) return undefined;
  return ids.map((id) => clientMap.get(id)?.name ?? id).join(', ');
}

function isActive(record: ManagedTableRecord): boolean {
  const value = record.fields.is_active;
  return value === true || value === 'true' || value === 1 || value === '1';
}

function entryName(entry: ManagedTableRecord): string {
  const combinedName = [fieldText(entry, 'First Name'), fieldText(entry, 'Last Name')]
    .filter((item): item is string => Boolean(item))
    .join(' ');
  return (
    fieldText(entry, 'Full Name') ??
    (combinedName || undefined) ??
    fieldText(entry, 'Email') ??
    entry.id
  );
}

function formatEntryDate(entry: ManagedTableRecord): string {
  const date = entryDate(entry);
  if (!date) return fieldText(entry, 'Submitted At') ?? fieldText(entry, 'Created At') ?? entry.createdTime;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDate(value: string | undefined): string | undefined {
  const date = parseDateValue(value);
  if (!date) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function entryDate(entry: ManagedTableRecord): Date | undefined {
  return (
    parseDateValue(fieldText(entry, 'Submitted At')) ??
    parseDateValue(fieldText(entry, 'Created At')) ??
    parseDateValue(entry.createdTime)
  );
}

function parseDateValue(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/\b(\d{1,2})(st|nd|rd|th)\b/gi, '$1');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function normalizeUrl(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i.test(trimmed)) return `https://${trimmed}`;
  return undefined;
}

function matchesFormsFilters(
  record: ManagedTableRecord,
  filters: {
    search: string;
    organization: string;
    active: string;
    source: string;
    purpose: string;
  },
): boolean {
  if (filters.organization !== 'All' && !relationIds(record.fields.org).includes(filters.organization)) return false;
  if (filters.active === 'Active' && !isActive(record)) return false;
  if (filters.active === 'Inactive' && isActive(record)) return false;
  if (filters.source !== 'All' && fieldText(record, 'source_system') !== filters.source) return false;
  if (filters.purpose !== 'All' && fieldText(record, 'form_purpose') !== filters.purpose) return false;
  return matchesSearch(record, filters.search, FORM_CONFIG.searchFields);
}

function matchesEntryFilters(
  record: ManagedTableRecord,
  filters: {
    search: string;
    organization: string;
    formName: string;
    dateFrom: string;
    dateTo: string;
  },
): boolean {
  if (filters.organization !== 'All' && !relationIds(record.fields.Organization_link).includes(filters.organization)) return false;
  if (filters.formName !== 'All' && fieldText(record, 'Form Name') !== filters.formName) return false;
  if (!matchesEntryDateRange(record, filters.dateFrom, filters.dateTo)) return false;
  return matchesSearch(record, filters.search, ENTRY_CONFIG.searchFields);
}

function matchesEntryDateRange(record: ManagedTableRecord, dateFrom: string, dateTo: string): boolean {
  if (!dateFrom && !dateTo) return true;
  const date = entryDate(record);
  if (!date) return false;
  const from = parseDateInputStart(dateFrom);
  const to = parseDateInputEnd(dateTo);
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

function parseDateInputStart(value: string): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function parseDateInputEnd(value: string): Date | undefined {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

function matchesSearch(record: ManagedTableRecord, search: string, fields: string[]): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return fields.some((field) => (fieldText(record, field) ?? '').toLowerCase().includes(query));
}

function buildFormsFilterOptions(records: ManagedTableRecord[], clientMap: Map<string, Client>) {
  return {
    organizations: buildOrganizationOptions(records, 'org', clientMap),
    sources: buildTextOptions(records, 'source_system'),
    purposes: buildTextOptions(records, 'form_purpose'),
  };
}

function buildEntriesFilterOptions(
  records: ManagedTableRecord[],
  clientMap: Map<string, Client>,
  formOptions: ManagementFilterOption[],
) {
  return {
    organizations: buildOrganizationOptions(records, 'Organization_link', clientMap),
    forms: formOptions,
  };
}

function buildOrganizationOptions(
  records: ManagedTableRecord[],
  field: 'org' | 'Organization_link',
  clientMap: Map<string, Client>,
): ManagementFilterOption[] {
  const counts = new Map<string, number>();
  for (const record of records) {
    for (const id of relationIds(record.fields[field])) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return [
    { value: 'All', label: 'All', count: records.length },
    ...Array.from(counts.entries())
      .sort(([a], [b]) => (clientMap.get(a)?.name ?? a).localeCompare(clientMap.get(b)?.name ?? b))
      .map(([id, count]) => ({
        value: id,
        label: clientMap.get(id)?.name ?? id,
        count,
      })),
  ];
}

function buildTextOptions(records: ManagedTableRecord[], field: string): ManagementFilterOption[] {
  const counts = new Map<string, number>();
  for (const record of records) {
    const value = fieldText(record, field);
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [
    { value: 'All', label: 'All', count: records.length },
    ...Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([value, count]) => ({ value, label: value, count })),
  ];
}

function buildFormOptions(entries: ManagedTableRecord[]): ManagementFilterOption[] {
  return buildTextOptions(entries, 'Form Name');
}

function buildEntryMetrics(entries: ManagedTableRecord[]) {
  const now = new Date();
  const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const yearToDate = new Date(now.getFullYear(), 0, 1);

  let last24Count = 0;
  let last7Count = 0;
  let ytdCount = 0;
  for (const entry of entries) {
    const date = entryDate(entry);
    if (!date || date > now) continue;
    if (date >= last24Hours) last24Count += 1;
    if (date >= last7Days) last7Count += 1;
    if (date >= yearToDate) ytdCount += 1;
  }

  return {
    last24Hours: last24Count,
    last7Days: last7Count,
    yearToDate: ytdCount,
  };
}

function buildActiveFilters({
  isEntriesView,
  search,
  organization,
  organizationLabel,
  active,
  source,
  purpose,
  formName,
  dateFrom,
  dateTo,
  onClear,
}: {
  isEntriesView: boolean;
  search: string;
  organization: string;
  organizationLabel?: string;
  active: string;
  source: string;
  purpose: string;
  formName: string;
  dateFrom: string;
  dateTo: string;
  onClear: (key: string, value: string) => void;
}): ManagementActiveFilter[] {
  const filters: ManagementActiveFilter[] = [];
  if (organization !== 'All') filters.push({ id: 'organization', label: 'Organization', value: organizationLabel ?? organization, onClear: () => onClear('organization', 'All') });
  if (search) filters.push({ id: 'search', label: 'Search', value: `"${search}"`, onClear: () => onClear('search', '') });
  if (isEntriesView) {
    if (formName !== 'All') filters.push({ id: 'form', label: 'Form', value: formName, onClear: () => onClear('form', 'All') });
    if (dateFrom) filters.push({ id: 'dateFrom', label: 'From', value: dateFrom, onClear: () => onClear('dateFrom', '') });
    if (dateTo) filters.push({ id: 'dateTo', label: 'To', value: dateTo, onClear: () => onClear('dateTo', '') });
  } else {
    if (active !== 'All') filters.push({ id: 'active', label: 'Active', value: active, onClear: () => onClear('active', 'All') });
    if (source !== 'All') filters.push({ id: 'source', label: 'Source', value: source, onClear: () => onClear('source', 'All') });
    if (purpose !== 'All') filters.push({ id: 'purpose', label: 'Purpose', value: purpose, onClear: () => onClear('purpose', 'All') });
  }
  return filters;
}

function withSearch(path: string, searchParams: URLSearchParams) {
  const qs = searchParams.toString();
  return qs ? `${path}?${qs}` : path;
}

function withoutViewParam(searchParams: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete('view');
  return next;
}

function sanitizePreviewHtml(value: string | undefined): string | undefined {
  if (!value?.trim()) return undefined;
  if (typeof DOMParser === 'undefined') return undefined;
  const doc = new DOMParser().parseFromString(value, 'text/html');
  doc.body.querySelectorAll('script,style,iframe,object,embed,link,meta').forEach((node) => node.remove());
  doc.body.querySelectorAll('*').forEach((node) => {
    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase();
      const attrValue = attr.value;
      if (
        name.startsWith('on') ||
        name === 'srcdoc' ||
        name === 'style' ||
        ((name === 'href' || name === 'src' || name === 'action') && !isSafePreviewUrl(attrValue))
      ) {
        node.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML.trim() || undefined;
}

function isSafePreviewUrl(value: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(value.trim());
}

function extractPreviewFields(value: string | undefined): string[] {
  if (!value?.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    const labels = new Set<string>();
    collectPreviewLabels(parsed, labels);
    return Array.from(labels).slice(0, 40);
  } catch {
    return [];
  }
}

function collectPreviewLabels(value: unknown, labels: Set<string>) {
  if (!value || labels.size >= 40) return;
  if (Array.isArray(value)) {
    for (const item of value) collectPreviewLabels(item, labels);
    return;
  }
  if (typeof value !== 'object') return;
  const record = value as Record<string, unknown>;
  const label = plainValue(record.label) ?? plainValue(record.name) ?? plainValue(record.title);
  if (label && !isTechnicalPreviewLabel(label)) labels.add(label);
  for (const child of Object.values(record)) collectPreviewLabels(child, labels);
}

function isTechnicalPreviewLabel(value: string): boolean {
  return ['id', 'type', 'required', 'choices', 'fields', 'inputs'].includes(value.trim().toLowerCase());
}
